import React, { useState } from "react";
import {
  Edit3,
  Trash2,
  Copy,
  MoreVertical,
  Eye,
  Calculator,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Percent,
  Scale,
  Shield,
  ChevronRight,
  Tag,
} from "lucide-react";

const PricingRuleCard = ({
  rule,
  isSelected,
  onSelect,
  onEdit,
  onClone,
  onDelete,
  onToggleStatus,
  onViewDetails,
  onTest,
}) => {
  const [showActions, setShowActions] = useState(false);

  // Check if rule is expiring soon (within 7 days)
  const isExpiringSoon = () => {
    if (!rule.validUntil) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(rule.validUntil) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  };

  // Check if expired
  const isExpired = () => {
    if (!rule.validUntil) return false;
    return new Date(rule.validUntil) < new Date();
  };

  // Days until expiry
  const getDaysUntilExpiry = () => {
    if (!rule.validUntil) return null;
    return Math.ceil(
      (new Date(rule.validUntil) - new Date()) / (1000 * 60 * 60 * 24)
    );
  };

  // Status configuration
  const getStatusConfig = () => {
    if (isExpired()) {
      return {
        label: "Expired",
        bgColor: "bg-danger/10",
        textColor: "text-danger",
        dotColor: "bg-danger",
      };
    }
    if (!rule.isActive) {
      return {
        label: "Inactive",
        bgColor: "bg-neutral-light/50",
        textColor: "text-neutral-dark",
        dotColor: "bg-neutral",
      };
    }
    return {
      label: "Active",
      bgColor: "bg-success/10",
      textColor: "text-success-dark",
      dotColor: "bg-success",
    };
  };

  // Priority configuration
  const getPriorityConfig = () => {
    if (rule.priority >= 80) {
      return { label: "Critical", color: "text-danger", bg: "bg-danger/10" };
    }
    if (rule.priority >= 50) {
      return { label: "High", color: "text-warning-dark", bg: "bg-warning/10" };
    }
    if (rule.priority >= 20) {
      return { label: "Medium", color: "text-primary", bg: "bg-primary/10" };
    }
    return { label: "Low", color: "text-neutral", bg: "bg-neutral-light/50" };
  };

  // Product type icons
  const getProductTypeIcon = (type) => {
    const icons = {
      GOLD: "🥇",
      SILVER: "🥈",
      DIAMOND: "💎",
      PLATINUM: "⚪",
      GEMSTONE: "💠",
    };
    return icons[type] || "📦";
  };

  const statusConfig = getStatusConfig();
  const priorityConfig = getPriorityConfig();

  // Format charge display
  const formatChargeValue = (charge) => {
    if (!charge || charge.value === null || charge.value === undefined)
      return "—";
    if (charge.type === "PERCENTAGE") return `${charge.value}%`;
    if (charge.type === "PER_GRAM") return `₹${charge.value}/g`;
    if (charge.type === "FIXED") return `₹${charge.value}`;
    return `${charge.value}`;
  };

  return (
    <div
      className={`group relative bg-white rounded-2xl transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isSelected
          ? "ring-2 ring-primary shadow-lg shadow-primary/10"
          : "shadow-sm hover:shadow-primary/10"
      } ${isExpiringSoon() ? "ring-2 ring-warning/50" : ""}`}
    >
      {/* Selection Checkbox - Floating */}
      <div className="absolute -top-2 -left-2 z-10">
        <button
          onClick={onSelect}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 shadow-md ${
            isSelected
              ? "bg-primary text-white scale-110"
              : "bg-white text-neutral-light hover:text-primary hover:scale-105 border border-neutral-light/50"
          }`}
        >
          {isSelected ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <div className="w-3 h-3 rounded-full border-2 border-current" />
          )}
        </button>
      </div>

      {/* Header Section */}
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between mb-3">
          {/* Left: Status & Priority */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig.bgColor} ${statusConfig.textColor}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotColor} ${
                  rule.isActive && !isExpired() ? "animate-pulse" : ""
                }`}
              />
              {statusConfig.label}
            </span>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig.bg} ${priorityConfig.color}`}
            >
              P{rule.priority}
            </span>
          </div>

          {/* Right: Actions Menu */}
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-1.5 rounded-lg text-neutral hover:text-neutral-dark hover:bg-neutral-light/50 transition-all"
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-neutral-light/50 py-1.5 z-30">
                  <button
                    onClick={() => {
                      onViewDetails();
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-dark hover:bg-neutral-lightest transition-colors"
                  >
                    <Eye className="w-4 h-4 text-neutral" />
                    <span>View Details</span>
                  </button>
                  <button
                    onClick={() => {
                      onEdit();
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-dark hover:bg-neutral-lightest transition-colors"
                  >
                    <Edit3 className="w-4 h-4 text-neutral" />
                    <span>Edit Rule</span>
                  </button>
                  <button
                    onClick={() => {
                      onClone();
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-dark hover:bg-neutral-lightest transition-colors"
                  >
                    <Copy className="w-4 h-4 text-neutral" />
                    <span>Clone Rule</span>
                  </button>
                  {onTest && (
                    <button
                      onClick={() => {
                        onTest();
                        setShowActions(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-dark hover:bg-neutral-lightest transition-colors"
                    >
                      <Calculator className="w-4 h-4 text-neutral" />
                      <span>Test Rule</span>
                    </button>
                  )}

                  <div className="my-1.5 border-t border-neutral-light/50" />

                  <button
                    onClick={() => {
                      onToggleStatus();
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-neutral-lightest transition-colors"
                  >
                    {rule.isActive ? (
                      <>
                        <XCircle className="w-4 h-4 text-warning" />
                        <span className="text-warning-dark">Deactivate</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 text-success" />
                        <span className="text-success-dark">Activate</span>
                      </>
                    )}
                  </button>

                  <div className="my-1.5 border-t border-neutral-light/50" />

                  <button
                    onClick={() => {
                      onDelete();
                      setShowActions(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger-light transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Rule</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Title & Description */}
        <div className="mb-4">
          <h3
            className="text-lg font-bold text-neutral-dark cursor-pointer hover:text-primary transition-colors line-clamp-1"
            onClick={onViewDetails}
          >
            {rule.name}
          </h3>
          {rule.description && (
            <p className="text-sm text-neutral mt-1 line-clamp-2">
              {rule.description}
            </p>
          )}
        </div>

        {/* Product Types Tags */}
        {rule.conditions?.productTypes?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {rule.conditions.productTypes.map((type, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-primary/5 to-secondary/5 text-primary rounded-md text-xs font-medium border border-primary/10"
              >
                <span>{getProductTypeIcon(type)}</span>
                {type}
              </span>
            ))}
            {rule.conditions.subTypes?.length > 0 && (
              <span className="px-2 py-0.5 bg-neutral-light/50 text-neutral-dark rounded-md text-xs">
                +{rule.conditions.subTypes.length} subtypes
              </span>
            )}
          </div>
        )}

        {/* Expiring Soon Alert */}
        {isExpiringSoon() && (
          <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/20 rounded-lg mb-4">
            <AlertCircle className="w-4 h-4 text-warning flex-shrink-0" />
            <span className="text-xs font-medium text-warning-dark">
              Expires in {getDaysUntilExpiry()} day
              {getDaysUntilExpiry() !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Charges Section - Highlighted */}
      <div className="px-5 pb-4">
        <div className="bg-gradient-to-br from-neutral-lightest via-white to-primary/5 rounded-xl p-4 border border-neutral-light/30">
          <div className="grid grid-cols-2 gap-3">
            {/* Making Charges */}
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-neutral">
                <Percent className="w-3.5 h-3.5" />
                <span>Making</span>
              </div>
              <p className="text-lg font-bold text-neutral-dark">
                {formatChargeValue(rule.charges?.makingCharges)}
              </p>
            </div>

            {/* Wastage */}
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-neutral">
                <Scale className="w-3.5 h-3.5" />
                <span>Wastage</span>
              </div>
              <p className="text-lg font-bold text-neutral-dark">
                {formatChargeValue(rule.charges?.wastageCharges)}
              </p>
            </div>

            {/* Hallmark */}
            {rule.charges?.hallmarkCharges?.enabled && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-neutral">
                  <Shield className="w-3.5 h-3.5" />
                  <span>Hallmark</span>
                </div>
                <p className="text-sm font-semibold text-neutral-dark">
                  {formatChargeValue(rule.charges.hallmarkCharges)}
                </p>
              </div>
            )}

            {/* GST */}
            {rule.gstConfig?.customRate && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-neutral">
                  <Tag className="w-3.5 h-3.5" />
                  <span>GST</span>
                </div>
                <p className="text-sm font-semibold text-neutral-dark">
                  {rule.gstConfig.customRate}%
                </p>
              </div>
            )}
          </div>

          {/* Weight Range */}
          {(rule.conditions?.minWeight || rule.conditions?.maxWeight) && (
            <div className="mt-3 pt-3 border-t border-neutral-light/50 flex items-center justify-between text-xs">
              <span className="text-neutral">Weight Range</span>
              <span className="font-medium text-neutral-dark">
                {rule.conditions.minWeight || 0}g -{" "}
                {rule.conditions.maxWeight
                  ? `${rule.conditions.maxWeight}g`
                  : "∞"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-neutral-lightest/50 border-t border-neutral-light/30 rounded-b-2xl flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-neutral">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>
              {new Date(rule.validFrom).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
              })}
              {rule.validUntil && (
                <>
                  {" → "}
                  {new Date(rule.validUntil).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </>
              )}
            </span>
          </div>
        </div>

        <button
          onClick={onViewDetails}
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-dark transition-colors group/btn"
        >
          <span>Details</span>
          <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Selection Highlight Overlay */}
      {isSelected && (
        <div className="absolute inset-0 bg-primary/5 rounded-2xl pointer-events-none" />
      )}
    </div>
  );
};

export default PricingRuleCard;
